import React, {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Popover } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { capitalize, orderBy, sortBy } from 'lodash';
import { SButton, SCheckbox, SIcon } from '@chargebee/sting-react';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/SegmentedControl';
import {
  BrandControl,
  BrandDisplayName,
} from '@bbkAdminComponents/experiences/branding/brand-dropdown';
import { useShowDemoData } from '@bbkAdminComponents/play/play-context';
import {
  GenericPopover,
  type DefaultActionButton,
  type ModalOnCloseReason,
} from '@bbkAdminComponents/popovers/generic-popover';
import TimePicker from '@bbkAdminComponents/popovers/time-picker';
import {
  GenericSelect,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import {
  Checkbox,
  CheckboxLabel,
} from '@bbkAdminComponents/switches/checkbox/checkbox-v2';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  useGetDemoSuffix,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import {
  ReportsFilterType,
  selectDeliveryReportFilter,
  selectExperiencesAudiencesPlaysReportFilter,
  selectReportFilterForPicker,
  type AudienceFilter,
  type DeliveryFilter,
  type ExperienceFilter,
  type PlayFilter,
} from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
  type BrandOptionType,
} from '@bbkAdminRedux/rtkq/brands.slice';
import {
  datepickerPersistSlice,
  useSelectDatePicker,
} from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  useSelectAllFunnels,
  useSelectAllFunnelsEntities,
  useSelectFunnelsLoading,
  type RestFunnel,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  PlayPresentation,
  playSlice,
  type Play,
} from '@bbkAdminRedux/rtkq/play.slice';
import {
  useSelectFunnelAudiencesHistory,
  type FunnelAudience,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  rulesSlice,
  RuleType,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import { OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { cs } from '@bbkAdminUtils/css-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { Icons } from '@bbkAdminUtils/icons';
import { useGetProductLicense, usePlans } from '@bbkAdminUtils/plans';
import {
  useSelectTimePeriods,
  type TimePickerStructure,
} from '@bbkAdminUtils/time-periods';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';

import '@bbkAdminComponents/reports-filter-picker/reports-filter-picker.scss';

const checkmark = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.7768 4.1394L7.14968 10.1488L4.45724 7.39925C4.3374 7.27745 4.16212 7.29845 4.063 7.44895C3.96557 7.59875 3.98293 7.81924 4.10276 7.94174L7.02032 10.9209C7.07072 10.972 7.134 11 7.19784 11C7.20904 11 7.22024 10.9993 7.23144 10.9979C7.30704 10.986 7.37592 10.937 7.42184 10.8614L11.2242 4.56149C11.3171 4.40679 11.2925 4.1877 11.1687 4.0715C11.0461 3.9539 10.8703 3.9847 10.7768 4.1394Z"
      stroke="white"
    />
  </svg>
);

const minus = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    xmlns="http://www.w3.org/2000/svg"
    className="tw-fill-white"
  >
    <path d="M11.5 7H3.5C3.22386 7 3 7.22386 3 7.5C3 7.77614 3.22386 8 3.5 8H11.5C11.7761 8 12 7.77614 12 7.5C12 7.22386 11.7761 7 11.5 7Z" />
  </svg>
);

type ReportCheckboxProps = {
  checked?: boolean;
  partiallyChecked?: boolean;
  onChange?: React.ComponentProps<typeof Checkbox>['onChange'];
  disabled?: boolean;
};
const ReportCheckbox: ReactFCC<ReportCheckboxProps> = ({
  checked,
  partiallyChecked,
  onChange,
  disabled,
  children,
}) => {
  const bold = !!(checked || partiallyChecked);
  const productLicense = useGetProductLicense();
  const growthV2License = productLicense.growthV2License;

  // TODO: Deprecate old checkbox after we completely migrate to react sting
  if (growthV2License) {
    return (
      <div className="tw-flex tw-select-none tw-items-center tw-text-sm tw-leading-7">
        <SCheckbox
          checked={checked || false}
          indeterminate={partiallyChecked}
          disabled={disabled}
          onCheckedChange={(newChecked) => {
            if (onChange && !disabled) {
              onChange({
                target: { checked: Boolean(newChecked) },
              } as React.ChangeEvent<HTMLInputElement>);
            }
          }}
        />
        <div className="tw-ml-1">{children}</div>
      </div>
    );
  }

  return (
    <CheckboxLabel
      className={cs('tw-select-none tw-text-sm tw-leading-7 tw-transition', {
        'tw-text-black': bold,
        'tw-text-black/50': !bold,
        'tw-cursor-not-allowed tw-opacity-50': disabled,
        '[&>div]:tw-mr-1': true,
      })}
    >
      <Checkbox
        size="tiny"
        checked={bold}
        icon={partiallyChecked ? minus : checkmark}
        onChange={onChange}
        disabled={disabled}
      />
      {children}
    </CheckboxLabel>
  );
};

export const Separator = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const ReportsFilterContext = createContext<{
  filterType: ReportsFilterType;
  setFilterType: React.Dispatch<React.SetStateAction<ReportsFilterType>>;

  brandFilter: string | undefined;
  setBrandFilter: React.Dispatch<React.SetStateAction<string | undefined>>;

  experiencesFilters: ExperienceFilter[];
  setExperiencesFilters: React.Dispatch<
    React.SetStateAction<ExperienceFilter[]>
  >;

  audiencesFilters: AudienceFilter[];
  setAudiencesFilters: React.Dispatch<React.SetStateAction<AudienceFilter[]>>;

  playsFilters: PlayFilter[];
  setPlaysFilters: React.Dispatch<React.SetStateAction<PlayFilter[]>>;

  brandCancelPagePlays: Play[];
  allCancelPagePlays: Play[];

  brandFunnels: RestFunnel[];
  faHistory: ReturnType<typeof useSelectFunnelAudiencesHistory>['data'];

  isFunnelsLoading: boolean;
  isAudiencesHistoryFetching: boolean;
  isPlaysLoading: boolean;
  isPlaysFetching: boolean;

  deliveryFilters: Set<DeliveryFilter>;
  setDeliveryFilters: React.Dispatch<React.SetStateAction<Set<DeliveryFilter>>>;

  timeRange: TimePickerStructure;
  setTimeRange: React.Dispatch<React.SetStateAction<TimePickerStructure>>;

  lastBrandFilter: string | undefined;
  setLastBrandFilter: React.Dispatch<React.SetStateAction<string | undefined>>;

  save: () => void;
  saveBrandChange: (
    brandId: string | undefined,
    lastBrandId: string | undefined
  ) => void;
  reset: () => void;
}>(undefined!);

type ReportFilterByValue = AudienceFilter | ExperienceFilter | PlayFilter;

/**
 * Compare filter arrays by full object (order-independent).
 * Used to skip setState when the logical selection is unchanged (avoids re-render loops).
 * Element-by-element comparison avoids delimiter collisions (e.g. if a value contained "|").
 */
function filterArraysEqual(
  a: ReportFilterByValue[],
  b: ReportFilterByValue[]
): boolean {
  if (a.length !== b.length) return false;
  const toKey = (x: ReportFilterByValue) => JSON.stringify(x);
  const aKeys = a.map(toKey).sort();
  const bKeys = b.map(toKey).sort();
  return aKeys.every((k, i) => k === bKeys[i]);
}

const ReportsFilterProvider: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const suffix = useGetDemoSuffix();
  const isDemoData = useShowDemoData();
  const productLicense = useGetProductLicense();
  const growthV2License = productLicense.growthV2License;
  const reportsFilter = useSelector(selectReportFilterForPicker(suffix));
  const [filterType, setFilterType] = useState(reportsFilter.type);
  const [brandFilter, setBrandFilter] = useState(reportsFilter.brandFilter);
  const [lastBrandFilter, setLastBrandFilter] = useState(
    reportsFilter.lastBrandFilter
  );

  const isBrandFilterEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  useEffect(() => {
    if (isPredictiveEnabled && brandFilter !== reportsFilter.brandFilter) {
      setBrandFilter(reportsFilter.brandFilter);
    }
  }, [reportsFilter.brandFilter]);

  useEffect(() => {
    if (!isBrandFilterEnabled && isDefined(brandFilter)) {
      setBrandFilter(undefined);
    }
  }, [isBrandFilterEnabled, brandFilter]);

  const [experiencesFilters, setExperiencesFilters] = useState(
    reportsFilter.experiencesFilters ?? []
  );
  const [audiencesFilters, setAudiencesFilters] = useState(
    reportsFilter.audiencesFilters ?? []
  );
  const [playsFilters, setPlaysFilters] = useState(
    reportsFilter.playsFilters ?? []
  );

  const [deliveryFilters, setDeliveryFilters] = useState<Set<DeliveryFilter>>(
    new Set(
      reportsFilter.deliveryFilters ?? [
        OfferPresentation.modal,
        OfferPresentation.pop_up,
        OfferPresentation.banner,
        // OfferPresentation.card,
      ]
    )
  );
  const origTimeRange = useSelectDatePicker();
  const [timeRange, setTimeRangeState] =
    useState<TimePickerStructure>(origTimeRange);
  const setTimeRange = useCallback(
    (timePicker: React.SetStateAction<TimePickerStructure>) => {
      setTimeRangeState(timePicker);
    },
    []
  );

  const appKey = useSelector(selectAppId);

  // Load ALL funnels (no brandId filter).
  const allFunnelsArgs = useMemo(
    () => ({
      appKey,
      context: isDemoData ? BBKRequestContext.reports_filter : undefined,
    }),
    [appKey, isDemoData]
  );
  const allFunnels = useSelectAllFunnels(allFunnelsArgs);
  const { isLoading: isFunnelsLoading } =
    useSelectFunnelsLoading(allFunnelsArgs);

  // Reusable filtering functions
  const filterFunnelsByBrand = useCallback(
    (funnels: typeof allFunnels, brandId: string | undefined) => {
      if (!brandId || brandId === 'all') {
        return funnels;
      }
      if (brandId === DEFAULT_BRAND_ID) {
        return funnels.filter((f) => !f.brand_id);
      }
      return funnels.filter((f) => f.brand_id === brandId);
    },
    []
  );

  const filterPlaysByBrand = useCallback(
    (plays: typeof allCancelPagePlays, brandId: string | undefined) => {
      if (!brandId || brandId === 'all') {
        return plays;
      }
      if (brandId === DEFAULT_BRAND_ID) {
        return plays.filter((play) => !play.brand_id);
      }
      return plays.filter((play) => play.brand_id === brandId);
    },
    []
  );

  const filterFaHistoryByBrand = useCallback(
    (faHistory: typeof allFaHistory, filteredFunnels: typeof allFunnels) => {
      if (!faHistory?.ranges) {
        return faHistory;
      }
      const filteredFunnelIds = new Set(
        filteredFunnels.filter((f) => f.id !== undefined).map((f) => f.id)
      );
      return {
        ...faHistory,
        ranges: faHistory.ranges.filter((r) => {
          const [funnelId] = r.funnel_audiences.split(',');
          return filteredFunnelIds.has(Number(funnelId));
        }),
      };
    },
    []
  );

  // Filter funnels by brand on frontend
  const brandFunnels = useMemo(
    () => filterFunnelsByBrand(allFunnels, brandFilter),
    [allFunnels, brandFilter, filterFunnelsByBrand]
  );

  // Load ALL funnel audiences history (no brandId filter)
  const allReportArgs = useMemo(
    () => ({
      appKey,
      context: BBKRequestContext.reports_filter,
    }),
    [appKey]
  );
  const { data: allFaHistory, isFetching: isAudiencesHistoryFetching } =
    useSelectFunnelAudiencesHistory(allReportArgs);

  // Filter funnel audiences history by brand on frontend
  const faHistory = useMemo(() => {
    if (!brandFilter || brandFilter === 'all') {
      return allFaHistory;
    }
    return filterFaHistoryByBrand(allFaHistory, brandFunnels);
  }, [allFaHistory, brandFunnels, brandFilter, filterFaHistoryByBrand]);

  // Load all active plays, filter by brand on frontend
  const {
    data: activePlays = [],
    isFetching: isActivePlaysFetching,
    isLoading: isActivePlaysLoading,
  } = playSlice.endpoints.listPlays.useQuery(
    growthV2License
      ? {
          appKey,
          context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
        }
      : skipToken
  );

  // Load all archived plays, filter by brand on frontend
  const {
    data: archivedPlays = [],
    isFetching: isArchivedPlaysFetching,
    isLoading: isArchivedPlaysLoading,
  } = playSlice.endpoints.listPlays.useQuery(
    growthV2License
      ? {
          appKey,
          context: isDemoData ? BBKRequestContext.lifecycle_reports : undefined,
          active: false,
        }
      : skipToken
  );

  const isPlaysLoading = isActivePlaysLoading || isArchivedPlaysLoading;
  const isPlaysFetching = isActivePlaysFetching || isArchivedPlaysFetching;
  const allPlays = useMemo(() => {
    return [...activePlays, ...archivedPlays];
  }, [activePlays, archivedPlays]);

  // Filter to cancel page plays and sort (active first, then archived at bottom, then by name)
  const allCancelPagePlays = useMemo(() => {
    return orderBy(
      allPlays.filter((p) => p.presentation === PlayPresentation.CANCEL_PAGE),
      [(p) => !p.active, (p) => p.name],
      ['asc', 'asc']
    );
  }, [allPlays]);

  // Filter plays by brand on frontend
  const brandCancelPagePlays = useMemo(
    () => filterPlaysByBrand(allCancelPagePlays, brandFilter),
    [allCancelPagePlays, brandFilter, filterPlaysByBrand]
  );

  const allFASelected =
    audiencesFilters.length === (faHistory?.ranges?.length ?? 0);

  const allExperiencesSelected =
    experiencesFilters.length === brandFunnels.length;

  // Pure function to compute experience filters without side effects
  const computeExperienceFilters = useCallback(
    (funnelsToUse: RestFunnel[]): ExperienceFilter[] => {
      return funnelsToUse
        .filter((f) => f.id !== undefined)
        .map((f) => ({
          field: 'funnel',
          operator: 'equals',
          value: f.id,
        }));
    },
    []
  );

  useEffect(() => {
    const targetExperiencesFilters =
      reportsFilter.experiencesFilters !== undefined &&
      reportsFilter.experiencesFilters.length > 0 &&
      reportsFilter.brandFilter === brandFilter
        ? reportsFilter.experiencesFilters
        : brandFunnels.length > 0
          ? computeExperienceFilters(brandFunnels)
          : [];

    setExperiencesFilters((prev) =>
      filterArraysEqual(prev, targetExperiencesFilters)
        ? prev
        : targetExperiencesFilters
    );
  }, [
    reportsFilter.experiencesFilters,
    reportsFilter.brandFilter,
    brandFilter,
    brandFunnels,
    computeExperienceFilters,
  ]);

  // Pure function to compute audience filters without side effects
  const computeAudiencesFilters = useCallback(
    (faHistoryToUse?: typeof faHistory): AudienceFilter[] => {
      if (!faHistoryToUse?.ranges) {
        return [];
      }
      return faHistoryToUse.ranges.map((r) => ({
        field: 'funnel_audiences',
        operator: 'equals',
        value: r.funnel_audiences,
      }));
    },
    []
  );

  useEffect(() => {
    if (isAudiencesHistoryFetching) return;
    const targetAudiencesFilters =
      reportsFilter.audiencesFilters !== undefined &&
      reportsFilter.audiencesFilters.length > 0 &&
      reportsFilter.brandFilter === brandFilter
        ? reportsFilter.audiencesFilters
        : faHistory?.ranges
          ? computeAudiencesFilters(faHistory)
          : [];
    setAudiencesFilters((prev) =>
      filterArraysEqual(prev, targetAudiencesFilters)
        ? prev
        : targetAudiencesFilters
    );
  }, [
    reportsFilter.audiencesFilters,
    reportsFilter.brandFilter,
    brandFilter,
    faHistory,
    isAudiencesHistoryFetching,
    computeAudiencesFilters,
  ]);

  // Pure function to compute play filters without side effects
  const computePlayFilters = useCallback((playsToUse: Play[]): PlayFilter[] => {
    return playsToUse.map((play) => ({
      field: 'play',
      operator: 'equals',
      value: play.id,
    }));
  }, []);

  useEffect(() => {
    if (isPlaysLoading || isPlaysFetching) return;
    const targetPlaysFilters =
      reportsFilter.playsFilters !== undefined &&
      reportsFilter.brandFilter === brandFilter
        ? reportsFilter.playsFilters
        : computePlayFilters(brandCancelPagePlays);
    setPlaysFilters((prev) =>
      filterArraysEqual(prev, targetPlaysFilters) ? prev : targetPlaysFilters
    );
  }, [
    reportsFilter.playsFilters,
    reportsFilter.brandFilter,
    brandFilter,
    isPlaysLoading,
    isPlaysFetching,
    brandCancelPagePlays,
    computePlayFilters,
  ]);

  const generateExperienceFilters = useCallback(():
    | ExperienceFilter[]
    | undefined => {
    const shouldSelectAll =
      allExperiencesSelected || experiencesFilters.length === 0;
    if (shouldSelectAll && brandFilter === undefined) {
      const allFilters = computeExperienceFilters(brandFunnels);
      setExperiencesFilters(allFilters);
      return undefined;
    }
    if (shouldSelectAll) {
      const allFilters = computeExperienceFilters(brandFunnels);
      setExperiencesFilters(allFilters);
      return allFilters;
    }

    return experiencesFilters;
  }, [
    allExperiencesSelected,
    experiencesFilters,
    brandFilter,
    brandFunnels,
    computeExperienceFilters,
  ]);

  const generateAudiencesFilters = useCallback(():
    | AudienceFilter[]
    | undefined => {
    const shouldSelectAll = allFASelected || audiencesFilters.length === 0;
    if (shouldSelectAll && brandFilter === undefined) {
      const allFilters = computeAudiencesFilters(faHistory);
      setAudiencesFilters(allFilters);
      return undefined;
    }
    if (shouldSelectAll) {
      const allFilters = computeAudiencesFilters(faHistory);
      setAudiencesFilters(allFilters);
      return allFilters;
    }
    return audiencesFilters;
  }, [
    allFASelected,
    audiencesFilters,
    brandFilter,
    faHistory,
    computeAudiencesFilters,
  ]);

  const generatePlayFilters = useCallback((): PlayFilter[] => {
    const allPlaysSelected =
      playsFilters.length === brandCancelPagePlays.length &&
      brandCancelPagePlays.length > 0;

    // Plays always need to return IDs (unlike experiences/audiences which can be undefined for "all")
    if (allPlaysSelected || playsFilters.length === 0) {
      // Compute filters without mutating state
      return computePlayFilters(brandCancelPagePlays);
    }
    return playsFilters;
  }, [playsFilters, brandCancelPagePlays, computePlayFilters]);

  const save = useCallback((): void => {
    dispatch(
      appPersistSlice.actions.saveReportsFilter({
        appKey,
        suffix,
        type: filterType,
        brandFilter,
        experiencesFilters: generateExperienceFilters(),
        audiencesFilters: generateAudiencesFilters(),
        playsFilters: generatePlayFilters(),
        deliveryFilters: Array.from(deliveryFilters),
      })
    );

    dispatch(
      datepickerPersistSlice.actions.saveDatepicker({
        appKey,
        suffix,
        ...timeRange,
      })
    );
  }, [
    dispatch,
    appKey,
    suffix,
    filterType,
    brandFilter,
    deliveryFilters,
    timeRange,
    generateExperienceFilters,
    generateAudiencesFilters,
    generatePlayFilters,
  ]);

  // Dedicated function for brand changes - only updates brandFilter, lastBrandFilter, and filters
  const saveBrandChange = useCallback(
    (brandId: string | undefined, lastBrandId: string | undefined): void => {
      // Filter data for the specified brand using reusable filtering functions
      const filteredFunnels = filterFunnelsByBrand(allFunnels, brandId);
      const filteredPlays = filterPlaysByBrand(allCancelPagePlays, brandId);
      const filteredFaHistory = filterFaHistoryByBrand(
        allFaHistory,
        filteredFunnels
      );

      // - When brandId === undefined (all brands), return undefined for experiences/audiences (all selected)
      const updatedExperienceFilters =
        brandId === undefined
          ? undefined
          : computeExperienceFilters(filteredFunnels);
      const updatedAudienceFilters =
        brandId === undefined
          ? undefined
          : computeAudiencesFilters(filteredFaHistory);
      const updatedPlayFilters = computePlayFilters(filteredPlays);

      dispatch(
        appPersistSlice.actions.saveReportsFilter({
          appKey,
          suffix,
          type: reportsFilter.type,
          brandFilter: brandId,
          lastBrandFilter: lastBrandId,
          experiencesFilters: updatedExperienceFilters,
          audiencesFilters: updatedAudienceFilters,
          playsFilters: updatedPlayFilters,
        })
      );
    },
    [
      dispatch,
      appKey,
      suffix,
      reportsFilter.type,
      allFunnels,
      allFaHistory,
      allCancelPagePlays,
      filterFunnelsByBrand,
      filterPlaysByBrand,
      filterFaHistoryByBrand,
      computeExperienceFilters,
      computeAudiencesFilters,
      computePlayFilters,
    ]
  );

  const reset = useCallback((): void => {
    setFilterType(reportsFilter.type);
    setBrandFilter(reportsFilter.brandFilter);
    if (reportsFilter.experiencesFilters !== undefined) {
      setExperiencesFilters(reportsFilter.experiencesFilters);
    }
    if (reportsFilter.audiencesFilters !== undefined) {
      setAudiencesFilters(reportsFilter.audiencesFilters);
    }
    if (reportsFilter.playsFilters !== undefined) {
      setPlaysFilters(reportsFilter.playsFilters);
    }
    setDeliveryFilters(
      new Set(
        reportsFilter.deliveryFilters ?? [
          OfferPresentation.modal,
          OfferPresentation.pop_up,
          OfferPresentation.banner,
        ]
      )
    );
    setTimeRange(origTimeRange);
  }, [
    reportsFilter.type,
    reportsFilter.brandFilter,
    reportsFilter.experiencesFilters,
    reportsFilter.audiencesFilters,
    reportsFilter.playsFilters,
    reportsFilter.deliveryFilters,
    origTimeRange,
    setTimeRange,
  ]);

  return (
    <ReportsFilterContext.Provider
      value={{
        filterType,
        setFilterType,

        brandFilter,
        setBrandFilter,

        experiencesFilters,
        setExperiencesFilters,

        audiencesFilters,
        setAudiencesFilters,

        playsFilters,
        setPlaysFilters,

        brandCancelPagePlays,
        allCancelPagePlays,

        brandFunnels,
        faHistory,

        isFunnelsLoading,
        isAudiencesHistoryFetching,
        isPlaysLoading,
        isPlaysFetching,

        deliveryFilters,
        setDeliveryFilters,

        timeRange,
        setTimeRange,

        lastBrandFilter,
        setLastBrandFilter,
        save,
        saveBrandChange,
        reset,
      }}
    >
      {children}
    </ReportsFilterContext.Provider>
  );
};

const BrandingFilter: React.FC<{
  isPredictiveEnabled?: boolean | undefined;
}> = ({ isPredictiveEnabled }) => {
  const {
    brandFilter,
    setBrandFilter,
    lastBrandFilter,
    setLastBrandFilter,
    saveBrandChange,
  } = useContext(ReportsFilterContext);
  const appKey = useSelector(selectAppId);

  const defaultChoice = {
    value: 'all',
    label: 'Show all',
  };
  const brandOptions = useGetBrandOptions({ appKey });

  const [brandList, setBrandList] = useState<BrandOptionType[]>([
    defaultChoice,
  ]);

  useEffect(() => {
    if (!brandOptions) return;
    const brandOptionsWithAll = [defaultChoice, ...brandOptions];

    setBrandList(brandOptionsWithAll);
  }, [brandOptions]);

  if (isPredictiveEnabled) {
    const isAllBrandsSelected = !brandFilter && !!lastBrandFilter;
    const handleChange = () => {
      setLastBrandFilter(brandFilter);
      const updatedAllSelectedBrand = !isAllBrandsSelected;
      const updatedBrandFilter = updatedAllSelectedBrand
        ? undefined
        : lastBrandFilter;
      setBrandFilter(updatedBrandFilter);
      // Filters will be automatically rebuilt by useEffect hooks in ReportsFilterProvider
      // Save brand change with computed filters
      saveBrandChange(updatedBrandFilter, brandFilter);
    };

    return (
      <div className="tw-flex tw-items-center">
        <label className="tw-mr-2 tw-flex tw-items-center tw-pt-2">
          <input
            className="tw-m-1"
            type="checkbox"
            checked={isAllBrandsSelected}
            onChange={handleChange}
          />
          View all brands
        </label>
        <GenericSelect
          short
          isSearchable={false}
          components={{ Control: BrandControl }}
          options={brandOptions}
          value={
            brandFilter
              ? brandList.find((b) => b.value === (brandFilter ?? 'all'))
              : defaultChoice
          }
          className={cs('tw-z-20', {
            'tw-pointer-events-none tw-opacity-50': isAllBrandsSelected,
          })}
          onChange={(option: SelectOption<string>) => {
            setLastBrandFilter(brandFilter);
            const brandID = option.value === 'all' ? undefined : option.value;
            setBrandFilter(brandID);
            // Filters will be automatically rebuilt by useEffect hooks in ReportsFilterProvider
            // Save brand change with computed filters
            saveBrandChange(brandID, lastBrandFilter);
          }}
          formatOptionLabel={(option) => {
            if (option.value === 'all') {
              const lastBrand = brandList.find(
                (b) => b.value === lastBrandFilter
              );

              return (
                <BrandDisplayName
                  label={lastBrand ? lastBrand.label : 'All brands'}
                  is_default={
                    lastBrand ? lastBrand.value === DEFAULT_BRAND_ID : false
                  }
                  targeting_enabled={
                    lastBrand ? lastBrand.data?.targeting_enabled : undefined
                  }
                />
              );
            }

            return (
              <BrandDisplayName
                label={option.label}
                is_default={option.value === DEFAULT_BRAND_ID}
                targeting_enabled={option.data?.targeting_enabled}
              />
            );
          }}
        />
      </div>
    );
  }
  return (
    <>
      <GenericSelect
        isSearchable
        value={
          brandFilter
            ? brandList.find((b) => b.value === (brandFilter ?? 'all'))
            : defaultChoice
        }
        onChange={(option: SelectOption<string>) => {
          const brandID = option.value === 'all' ? undefined : option.value;
          setBrandFilter(brandID);
        }}
        placeholder="Choose brand"
        options={brandList}
      />
    </>
  );
};

const ExperiencesFilter: React.FC = () => {
  const {
    experiencesFilters,
    setExperiencesFilters,
    brandFunnels,
    isFunnelsLoading,
  } = useContext(ReportsFilterContext);
  const sortedFunnels = useMemo(
    () =>
      orderBy(
        brandFunnels,
        [(f) => f.is_control, (f) => !f.active, (f) => f.display_name],
        ['desc']
      ),
    [brandFunnels]
  );
  const testFunnels = useMemo(
    () => sortedFunnels.filter((f) => !f.is_control),
    [sortedFunnels]
  );

  const everyoneElse = (): React.ReactNode => {
    const testFunnelsIds = testFunnels.map((f) => f.id);
    const selectedFunnels = testFunnels.filter((f) => {
      if (f.is_control) return false;
      return experiencesFilters.find((paf) => paf.value === f.id);
    });
    const someSelected = selectedFunnels.length !== 0;
    const allSelected =
      testFunnels.length > 0 && selectedFunnels.length === testFunnels.length;
    return (
      <ReportCheckbox
        partiallyChecked={!allSelected && someSelected}
        checked={allSelected}
        onChange={(e) => {
          const nonTestFilters =
            experiencesFilters.filter(
              (paf) => !testFunnelsIds.includes(paf.value)
            ) ?? [];
          if (e.target.checked) {
            setExperiencesFilters([
              ...nonTestFilters,
              ...testFunnels.map((f) => ({
                field: 'funnel' as const,
                operator: 'equals' as const,
                value: f.id,
              })),
            ]);
          } else {
            setExperiencesFilters(nonTestFilters);
          }
        }}
      >
        <span className={cs({ 'tw-font-medium': false })}>
          {NavIcons.audiences} Fallback
        </span>
      </ReportCheckbox>
    );
  };

  if (isFunnelsLoading) return <>Loading...</>;
  if (!experiencesFilters) {
    GlobalLogger.error('filters are undefined');
    return <>Error, try to refresh page</>;
  }

  let archivedLabelRendered = false;
  let fallbackRendered = false;
  return (
    <>
      {sortedFunnels.map((f) => {
        const filterIdx = experiencesFilters.findIndex(
          (paf) => paf.value === f.id
        );
        const checked = filterIdx !== -1;
        const renderArchiveLabel = (): React.ReactNode => {
          if (archivedLabelRendered) return null;
          archivedLabelRendered = true;
          return (
            <span className="tw-text-xs tw-uppercase tw-text-black/30">
              Archived
            </span>
          );
        };
        const isFirstFallback = !f.is_control && !fallbackRendered;
        if (isFirstFallback) {
          fallbackRendered = true;
        }
        return (
          <Fragment key={f.id}>
            {isFirstFallback && <div>{everyoneElse()}</div>}
            <div className={cs({ 'tw-ml-5': !f.is_control })}>
              {!f.active && renderArchiveLabel()}
              <ReportCheckbox
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) {
                    setExperiencesFilters([
                      ...experiencesFilters,
                      {
                        field: 'funnel',
                        operator: 'equals',
                        value: f.id,
                      },
                    ]);
                  } else {
                    setExperiencesFilters([
                      ...experiencesFilters.slice(0, filterIdx),
                      ...experiencesFilters.slice(filterIdx + 1),
                    ]);
                  }
                }}
              >
                <span
                  className={cs('tw-truncate', { 'tw-font-medium': checked })}
                  title={f.display_name}
                >
                  {f.is_control ? NavIcons.holdoutPage : NavIcons.pages}{' '}
                  {f.display_name}
                </span>
              </ReportCheckbox>
            </div>
          </Fragment>
        );
      })}
    </>
  );
};

const AudiencesFilter: React.FC = () => {
  const {
    audiencesFilters,
    setAudiencesFilters,
    faHistory,
    isAudiencesHistoryFetching,
    isFunnelsLoading,
  } = useContext(ReportsFilterContext);

  const appKey = useSelector(selectAppId);
  const isDemoData = useShowDemoData();

  const rulesArgs = {
    appKey,
    context: BBKRequestContext.reports_filter,
    includeAdditionalRules: [RuleType.ALL_AUDIENCE],
  };
  const { isFetching: areRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const allRulesMap = useSelectAllRulesEntities(rulesArgs);

  const funnelsArgs = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
  };
  const allFunnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isLoading =
    areRulesFetching || isAudiencesHistoryFetching || isFunnelsLoading;

  const flatHistory = useMemo(() => {
    if (isLoading) return [];
    return sortBy(
      (faHistory?.ranges ?? [])
        .map((r) => r.funnel_audiences)
        .filter(isDefined),
      (fa) => {
        const [fId, ...group] = fa.split(',');
        const funnelName = allFunnelsMap[fId!]?.display_name ?? '~unknown';

        const names = group.map((g) => {
          if (g === 'control') return '!control';
          if (g === 'fallback') return '~1fallback';
          if (g === 'other') return '~2other';
          if (g === '') return '~3legacy';
          return allRulesMap[g]?.display_name ?? '~4unknown';
        });

        return [...names, names.length * -1, funnelName].join();
      },
      'desc'
    );
  }, [isLoading, allFunnelsMap, allRulesMap, faHistory]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const isCollapsableGroup = (groupKeyStr: string): boolean =>
    collapsed[groupKeyStr] !== undefined;

  useEffect(() => {
    setCollapsed(
      Object.fromEntries(
        flatHistory.map((fa) => [fa.split(',')[1], true] as [string, boolean])
      )
    );
  }, [flatHistory]);

  const getGroupFAs = (group: string[]): FunnelAudience[] =>
    flatHistory.filter(
      (f) =>
        f
          .split(',')
          .slice(1, group.length + 1)
          .join(',') === group.join(',')
    );

  const onChangeGroup = (group: string[], checked: boolean): void => {
    const toProcess = getGroupFAs(group);
    const otherAudienceFilters = audiencesFilters.filter(
      (af) => !toProcess.includes(af.value)
    );

    if (checked) {
      setAudiencesFilters([
        ...otherAudienceFilters,
        ...toProcess.map(
          (fa) =>
            ({
              field: 'funnel_audiences',
              operator: 'equals',
              value: fa,
            }) as AudienceFilter
        ),
      ]);
    } else {
      setAudiencesFilters(otherAudienceFilters);
    }
  };

  const lastRenderedGroup: string[] = [];

  const isCollapsed = (groupKey: string): boolean =>
    Boolean(collapsed[groupKey]);

  const renderNumberPages = (numPages: number): string =>
    `${numPages} ${numPages === 1 ? 'page' : 'pages'}`;

  const renderFA = (fa: FunnelAudience): React.ReactNode => {
    const [fId, ...groups] = fa.split(',');
    const funnel = allFunnelsMap[fId!];
    if (!funnel) return null;
    const filterIdx = audiencesFilters.findIndex((paf) => paf.value === fa);
    const checked = filterIdx !== -1;
    const topGroup = groups[0]!;
    return (
      <div key={fa}>
        {groups.map((g, idx, arr) => {
          if (lastRenderedGroup[idx] === g) return null;
          lastRenderedGroup[idx] = g;
          const title =
            g === 'control'
              ? 'Control'
              : g === 'fallback'
                ? 'Fallback'
                : g === 'other'
                  ? 'Other'
                  : g === ''
                    ? 'Legacy'
                    : (allRulesMap[g]?.display_name ?? 'Unknown');
          const groupKey = arr.slice(0, idx + 1);
          const groupKeyStr = groupKey.join(',');
          const groupFAs = getGroupFAs(groupKey);
          const selectedGroupFAs = groupFAs.filter((f) =>
            audiencesFilters.find((af) => af.value === f)
          );
          const someSelected = selectedGroupFAs.length !== 0;
          const allSelected =
            groupFAs.length > 0 && selectedGroupFAs.length === groupFAs.length;
          const isGroupCollapsed =
            groupKeyStr !== topGroup && isCollapsed(topGroup);
          return (
            <div
              key={`${fa}-${g}`}
              className={cs('tw-overflow-hidden tw-transition-all', {
                'tw-ml-5': idx === 1,
                'tw-ml-10': idx === 2,
                'tw-flex tw-justify-between': isCollapsableGroup(groupKeyStr),
                'tw-h-0': isGroupCollapsed,
                'tw-pb-px': !isGroupCollapsed, // for animation
              })}
            >
              <div className="tw-overflow-hidden">
                <ReportCheckbox
                  partiallyChecked={!allSelected && someSelected}
                  checked={allSelected}
                  onChange={(e) => onChangeGroup(groupKey, e.target.checked)}
                >
                  <span
                    className={cs('tw-truncate', { 'tw-font-medium': checked })}
                    title={title}
                  >
                    {g === 'control'
                      ? NavIcons.holdoutPage
                      : NavIcons.audiences}{' '}
                    {title}
                  </span>
                </ReportCheckbox>
              </div>
              {isCollapsableGroup(groupKeyStr) && (
                <div
                  className="tw-flex tw-cursor-pointer tw-select-none tw-items-center"
                  onClick={() =>
                    setCollapsed({
                      ...collapsed,
                      [groupKeyStr]: !collapsed[groupKeyStr],
                    })
                  }
                >
                  <span className="tw-mr-2 tw-whitespace-nowrap tw-text-xs tw-text-black/50">
                    {renderNumberPages(groupFAs.length)}
                  </span>
                  <span className="tw-text-cbrprimary-600">
                    <div
                      className={cs('tw-inline-block tw-transition-all', {
                        'tw--scale-y-100': !collapsed[topGroup],
                      })}
                    >
                      {Icons.chevronDownSmall}
                    </div>
                  </span>
                </div>
              )}
            </div>
          );
        })}
        <div
          className={cs('tw-overflow-hidden tw-transition-all', {
            'tw-ml-5': groups.length === 1,
            'tw-ml-10': groups.length === 2,
            'tw--ml-1 tw-pl-16': groups.length === 3,
            'tw-h-0': isCollapsed(topGroup),
            'tw-pb-px': !isCollapsed(topGroup),
          })}
        >
          <ReportCheckbox
            checked={checked}
            onChange={(e) => {
              if (e.target.checked) {
                setAudiencesFilters([
                  ...audiencesFilters,
                  {
                    field: 'funnel_audiences',
                    operator: 'equals',
                    value: fa,
                  },
                ]);
              } else {
                setAudiencesFilters([
                  ...audiencesFilters.slice(0, filterIdx),
                  ...audiencesFilters.slice(filterIdx + 1),
                ]);
              }
            }}
          >
            <span
              className={cs('tw-truncate', { 'tw-font-medium': checked })}
              title={funnel.display_name}
            >
              {NavIcons.pages} {funnel.display_name}
            </span>
          </ReportCheckbox>
        </div>
      </div>
    );
  };

  if (isLoading) return <>Loading...</>;
  if (!audiencesFilters) {
    GlobalLogger.error('filters are undefined');
    return <>Error happened, try to refresh page</>;
  }
  return <>{flatHistory.map((fa) => renderFA(fa))}</>;
};

const PlaysFilter: React.FC = () => {
  const { playsFilters, setPlaysFilters, brandCancelPagePlays } =
    useContext(ReportsFilterContext);
  if (!playsFilters) {
    GlobalLogger.error('filters are undefined');
    return <>Error, try to refresh page</>;
  }

  const selectedPlays = brandCancelPagePlays.filter((play) =>
    playsFilters.find((pf) => pf.value === play.id)
  );
  const someSelected = selectedPlays.length !== 0;
  const allSelected = selectedPlays.length === brandCancelPagePlays.length;

  let archivedLabelRendered = false;
  const renderArchiveLabel = (): React.ReactNode => {
    if (archivedLabelRendered) return null;
    archivedLabelRendered = true;
    return (
      <span className="tw-text-xs tw-uppercase tw-text-black/30">Archived</span>
    );
  };

  return (
    <>
      <div className="tw-mt-2">
        <ReportCheckbox
          partiallyChecked={!allSelected && someSelected}
          checked={allSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setPlaysFilters(
                brandCancelPagePlays.map((play) => ({
                  field: 'play',
                  operator: 'equals',
                  value: play.id,
                }))
              );
            } else {
              setPlaysFilters([]);
            }
          }}
        >
          <span>{NavIcons.pages} Plays</span>
        </ReportCheckbox>
      </div>
      {brandCancelPagePlays.map((play) => {
        const filterIdx = playsFilters.findIndex((pf) => pf.value === play.id);
        const checked = filterIdx !== -1;
        return (
          <div key={play.id} className="tw-ml-5">
            {!play.active && renderArchiveLabel()}
            <ReportCheckbox
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  setPlaysFilters([
                    ...playsFilters,
                    {
                      field: 'play',
                      operator: 'equals',
                      value: play.id,
                    },
                  ]);
                } else {
                  setPlaysFilters([
                    ...playsFilters.slice(0, filterIdx),
                    ...playsFilters.slice(filterIdx + 1),
                  ]);
                }
              }}
            >
              <span
                className={cs('tw-truncate', { 'tw-font-medium': checked })}
                title={play.name}
              >
                {play.name}
              </span>
            </ReportCheckbox>
          </div>
        );
      })}
    </>
  );
};

const DateFilter: React.FC<{ isLCOReportsFilter?: boolean }> = ({
  isLCOReportsFilter,
}) => {
  const isDemoData = useShowDemoData();
  const { timeRange, setTimeRange } = useContext(ReportsFilterContext);
  const { isStarterFreePlan } = usePlans();
  const showAllTimeRanges = !isStarterFreePlan;

  const timeValues = useSelectTimePeriods();

  return (
    <div className="[&_.time-picker-v2]:tw-block [&_.time-picker-v2_.bbk-date-picker>span]:tw-hidden [&_.time-picker-v2_.bbk-date-picker]:tw-mt-2 [&_.time-picker-v2_.bbk-date-picker]:tw-flex [&_.time-picker-v2_.bbk-date-picker]:tw-flex-wrap [&_.time-picker-v2_.bbk-date-picker]:tw-justify-between [&_.time-picker-v2_.bbk-date-picker]:tw-gap-2 [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label-text]:tw-block [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-flex-1 [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-rounded [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-border [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-border-black/10 [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-py-1.5 [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-pl-4 [&_.time-picker-v2_.bbk-date-picker_.bbk-date-picker-label]:tw-pr-1.5 [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input#flatpickr-first]:tw-text-left [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input:before]:tw-block [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input:before]:tw-content-['Start_date'] [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input]:tw-h-auto [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input]:tw-w-full [&_.time-picker-v2_.bbk-date-picker_.flatpickr-input]:tw-text-black [&_.time-picker-v2_.timeAgoButton>span>svg]:tw-hidden [&_.time-picker-v2_.timeAgoButton>span]:tw-flex [&_.time-picker-v2_.timeAgoButton>span]:tw-items-center [&_.time-picker-v2_.timeAgoButton]:tw-flex [&_.time-picker-v2_.timeAgoButton]:tw-justify-between [&_.time-picker-v2_.timeAgoButton]:tw-border [&_.time-picker-v2_.timeAgoButton]:tw-border-black/10 [&_.time-picker-v2_.timeAgoButton]:tw-py-1.5 [&_.time-picker-v2_.timeAgoButton]:tw-pl-4 [&_.time-picker-v2_.timeAgoButton]:tw-pr-1.5 [&_.time-picker-v2_.timeAgoButton]:tw-text-black">
      <TimePicker
        className="time-picker-v2"
        save={setTimeRange}
        readOnly={!showAllTimeRanges}
        disableButton={isDemoData && !isLCOReportsFilter}
        timePicker={timeRange}
        timeValues={timeValues}
      />
    </div>
  );
};

const deliveryFilterOptions = [
  {
    label: 'Modal',
    value: OfferPresentation.modal,
  },
  {
    label: 'Pop-up',
    value: OfferPresentation.pop_up,
  },
  {
    label: 'Banner',
    value: OfferPresentation.banner,
  },
];
type ReportsFilterProps = {
  isLCOReportsFilter?: boolean;
  showDeliveryFilter?: boolean;
  disabled?: boolean;
};

const reportsFilterOptions: SegmentedControlOption<ReportsFilterType>[] = [
  {
    label: 'Plays',
    value: ReportsFilterType.plays,
  },
  {
    label: 'Experiences',
    value: ReportsFilterType.experiences,
  },
  {
    label: 'Audiences',
    value: ReportsFilterType.audiences,
  },
];

const ReportsFilter: React.FC<ReportsFilterProps> = ({
  isLCOReportsFilter,
  showDeliveryFilter,
  disabled,
}) => {
  const { isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  const isStarterPlan = isStarterFreePlan || isStarterPremiumPlan;
  const showExtendedFilters = !isStarterPlan;

  const productLicense = useGetProductLicense();
  const growthV2License = productLicense.growthV2License;

  const isBrandFilterEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);

  const {
    filterType,
    setFilterType,

    experiencesFilters,
    setExperiencesFilters,

    playsFilters,
    setPlaysFilters,

    brandCancelPagePlays,

    audiencesFilters,
    setAudiencesFilters,

    brandFunnels,
    faHistory,

    deliveryFilters,
    setDeliveryFilters,

    save,
    reset,
  } = useContext(ReportsFilterContext);

  const suffix = useGetDemoSuffix();
  const { type: appliedFilterType } = useSelector(
    selectReportFilterForPicker(suffix)
  );
  const appliedFilters = useSelector(
    selectExperiencesAudiencesPlaysReportFilter(suffix)
  );
  const appliedDeliveryFilters = useSelector(
    selectDeliveryReportFilter(suffix)
  );

  const appliedFiltersLength = appliedFilters.length;

  const [open, setOpen] = useState(false);
  const anchorPositionRef = useRef<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const handlePopoverClose = (reason?: ModalOnCloseReason): void => {
    setOpen(false);
    if (reason) setTimeout(() => reset(), 500);
  };

  const handleOpen: React.MouseEventHandler<HTMLElement> = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    anchorPositionRef.current.top = rect.bottom;
    anchorPositionRef.current.left = rect.right;
    setOpen(true);
  };

  const appliedDeliveryFiltersLength = appliedDeliveryFilters.length;

  const isAllFiltersSelected = useCallback((): boolean => {
    if (appliedFiltersLength === 0) return true;
    switch (appliedFilterType) {
      case ReportsFilterType.plays:
        return appliedFiltersLength === brandCancelPagePlays.length;
      case ReportsFilterType.experiences:
        return appliedFiltersLength === brandFunnels.length;
      case ReportsFilterType.audiences:
        return appliedFiltersLength === (faHistory?.ranges?.length ?? 0);
      default:
        return false;
    }
  }, [
    appliedFilterType,
    appliedFiltersLength,
    brandCancelPagePlays.length,
    brandFunnels.length,
    faHistory?.ranges?.length,
  ]);

  const ActionButton = useMemo(
    () => (localProps: React.ComponentProps<typeof DefaultActionButton>) => {
      return (
        <button
          className="timeAgoButton"
          type="button"
          {...localProps}
          disabled={disabled}
        >
          <span className="tw-ml-1 tw-mr-2 tw-inline-block tw-self-baseline">
            {Icons.funnel}
          </span>
          {isLCOReportsFilter ? (
            showDeliveryFilter ? (
              <>
                <span>Filters</span>
                <div
                  className={cs(
                    'tw-ml-1 tw-flex tw-px-2 tw-py-1 tw-text-xs tw-font-bold',
                    'tw-rounded-full tw-border tw-border-cbrprimary-500 tw-bg-cbrprimary-50 tw-text-cbrprimary-700'
                  )}
                >
                  Delivery (
                  {appliedDeliveryFiltersLength ===
                    deliveryFilterOptions.length ||
                  appliedDeliveryFiltersLength === 0
                    ? 'all'
                    : appliedDeliveryFiltersLength}
                  )
                </div>
              </>
            ) : (
              <span>Date</span>
            )
          ) : growthV2License ? (
            <div className="tw-flex tw-flex-row">
              <span className="tw-px-2">Filters</span>
              <div className="tw-ml-1 tw-flex tw-rounded-full tw-bg-primary-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-primary-600">
                {capitalize(appliedFilterType)} (
                {isAllFiltersSelected() ? 'all' : appliedFiltersLength})
              </div>
            </div>
          ) : (
            <>
              Filters{' '}
              <div className="tw-ml-1 tw-flex tw-rounded-full tw-bg-cbrprimary-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-cbrprimary-600">
                {appliedFilterType === ReportsFilterType.experiences
                  ? 'Experiences '
                  : 'Audiences '}
                ({isAllFiltersSelected() ? 'all' : appliedFiltersLength})
              </div>
            </>
          )}
          {Icons.doubleChevron}
        </button>
      );
    },
    [
      appliedFilterType,
      appliedFiltersLength,
      appliedDeliveryFiltersLength,
      isAllFiltersSelected,
      disabled,
    ]
  );

  const allFASelected =
    audiencesFilters.length === (faHistory?.ranges?.length ?? 0);
  const someFASelected = audiencesFilters.length !== 0;

  const allExperiencesSelected =
    experiencesFilters.length === brandFunnels.length &&
    brandFunnels.length > 0;
  const someExperiencesSelected = experiencesFilters.length !== 0;

  const allPlaysSelected =
    playsFilters.length === brandCancelPagePlays.length &&
    brandCancelPagePlays.length > 0;
  const somePlaysSelected = playsFilters.length !== 0;

  const allSelected =
    filterType === ReportsFilterType.experiences
      ? allExperiencesSelected
      : filterType === ReportsFilterType.plays
        ? allPlaysSelected
        : allFASelected;
  const someSelected =
    filterType === ReportsFilterType.experiences
      ? someExperiencesSelected
      : filterType === ReportsFilterType.plays
        ? somePlaysSelected
        : someFASelected;
  const partiallyChecked = !allSelected && someSelected;
  const isListEmpty =
    filterType === ReportsFilterType.experiences
      ? brandFunnels.length === 0
      : filterType === ReportsFilterType.plays
        ? brandCancelPagePlays.length === 0
        : (faHistory?.ranges?.length ?? 0) === 0;

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;

      switch (filterType) {
        case ReportsFilterType.plays:
          if (checked) {
            setPlaysFilters(
              brandCancelPagePlays.map((play) => ({
                field: 'play',
                operator: 'equals',
                value: play.id,
              }))
            );
          } else {
            setPlaysFilters([]);
          }
          break;
        case ReportsFilterType.experiences:
          if (checked) {
            setExperiencesFilters(
              brandFunnels.map((funnel) => ({
                field: 'funnel',
                operator: 'equals',
                value: funnel.id,
              }))
            );
          } else {
            setExperiencesFilters([]);
          }
          break;
        case ReportsFilterType.audiences:
          if (checked) {
            setAudiencesFilters(
              (faHistory?.ranges ?? []).map((fa) => ({
                field: 'funnel_audiences',
                operator: 'equals',
                value: fa.funnel_audiences,
              }))
            );
          } else {
            setAudiencesFilters([]);
          }
          break;
        default:
          break;
      }
    },
    [
      filterType,
      brandFunnels,
      faHistory,
      brandCancelPagePlays,
      setExperiencesFilters,
      setAudiencesFilters,
      setPlaysFilters,
    ]
  );

  // Renders Retention filters
  const renderV1FiltersTab = () => (
    <div className="tw-my-2 tw-px-6">
      <ReportCheckbox
        checked={allSelected}
        partiallyChecked={partiallyChecked}
        onChange={handleSelectAllChange}
        disabled={isListEmpty}
      >
        <div className="!tw-mr-0 tw-flex tw-w-full tw-items-center tw-justify-between">
          <span className="tw-mx-1 tw-font-medium">Filter by</span>
          <div
            className="tw-flex tw-flex-1 tw-overflow-clip tw-rounded-3xl tw-border tw-border-cbrneutral-200 tw-text-center tw-text-xs"
            onClick={(e) => e.preventDefault()}
          >
            <div
              className={cs(
                'tw-w-1/2 tw-rounded-2xl tw-py-1.5 tw-duration-500',
                {
                  'tw-bg-cbrprimary-50 tw-font-medium tw-text-cbrprimary-600':
                    filterType === ReportsFilterType.experiences,
                }
              )}
              onClick={() => setFilterType(ReportsFilterType.experiences)}
            >
              Experiences
            </div>
            <div
              className={cs(
                'tw-w-1/2 tw-rounded-2xl tw-py-1.5 tw-duration-500',
                {
                  'tw-bg-cbrprimary-50 tw-font-medium tw-text-cbrprimary-600':
                    filterType === ReportsFilterType.audiences,
                }
              )}
              onClick={() => setFilterType(ReportsFilterType.audiences)}
            >
              Audiences
            </div>
          </div>
        </div>
      </ReportCheckbox>
    </div>
  );

  // Renders new filter following React Sting with Play Filter (Growth sites only)
  const renderGrowthFiltersTab = () => (
    <div className="tw-mt-2 tw-flex tw-flex-col tw-px-6">
      <span className="tw-my-2 tw-text-sm tw-font-medium">Filter by</span>
      <SegmentedControl
        value={filterType}
        onChange={setFilterType}
        options={reportsFilterOptions}
        showDividers
      />
      {
        // Can re-enable for plays when/if we start supporting global control/fallback options, currently redundant
        filterType !== ReportsFilterType.plays && (
          <div className="tw-mt-2">
            <ReportCheckbox
              checked={allSelected}
              partiallyChecked={partiallyChecked}
              onChange={handleSelectAllChange}
              disabled={isListEmpty}
            >
              <span className="tw-mx-1 tw-mt-1 tw-font-medium">Select all</span>
            </ReportCheckbox>
          </div>
        )
      }
    </div>
  );

  return (
    <>
      <ActionButton onClick={handleOpen} disabled={disabled} />
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={anchorPositionRef.current}
        onClose={(_, reason) => handlePopoverClose(reason)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: { width: '20rem', overflow: 'initial' },
        }}
        classes={{ paper: 'bbk-generic-dialog-popover' }}
        disableEnforceFocus
      >
        {(() => {
          const handleClose = (): void => setOpen(false);
          const handleSave = (): void => {
            save();
            handleClose();
          };

          const handleCancel = (): void => {
            reset();
            handleClose();
          };

          return (
            <div className="[&_svg]:tw-fill-black/50">
              <div className="bbk-gdialog-header tw-px-5 tw-py-4">
                <div className="bbk-gdialog-title">Filters</div>
                <div className="bbk-gdialog-header-actions">
                  {growthV2License ? (
                    <div className="tw-flex tw-gap-3">
                      <SButton
                        variant="neutral-outline"
                        size="large"
                        onClick={handleCancel}
                      >
                        Cancel
                      </SButton>
                      <SButton
                        variant="primary"
                        size="large"
                        onClick={handleSave}
                      >
                        Apply
                      </SButton>
                    </div>
                  ) : (
                    <>
                      <ButtonV2
                        secondary
                        className="bbk-gdialog-button tw-mr-2"
                        onClick={handleCancel}
                      >
                        Cancel
                      </ButtonV2>
                      <ButtonV2
                        primary
                        className="bbk-gdialog-button"
                        onClick={handleSave}
                      >
                        Apply
                      </ButtonV2>
                    </>
                  )}
                </div>
              </div>
              <Separator />
              <div className="tw-my-3 tw-px-6">
                <div className="tw-mb-2 tw-text-sm tw-text-cbrneutral-500">
                  Date filter
                </div>
                <DateFilter isLCOReportsFilter={isLCOReportsFilter} />
              </div>
              {/* TODO: Eventually deprecate LCO Filter once Growth V2 takes over */}
              {isLCOReportsFilter ? (
                showDeliveryFilter ? (
                  <>
                    <Separator />
                    <div className="tw-my-4 tw-px-6">
                      <div className="tw-mb-2 tw-text-sm tw-text-cbrneutral-500">
                        Filter by
                      </div>
                      <GenericSelect
                        options={[
                          {
                            label: 'Delivery',
                            value: ReportsFilterType.delivery,
                          },
                        ]}
                        value={{
                          label: 'Delivery',
                          value: ReportsFilterType.delivery,
                        }}
                        onChange={() =>
                          setFilterType(ReportsFilterType.delivery)
                        }
                      />
                      <div className="tw-pt-4">
                        <ReportCheckbox
                          checked={
                            deliveryFilters.size ===
                            deliveryFilterOptions.length
                          }
                          partiallyChecked={
                            deliveryFilters.size > 0 &&
                            deliveryFilters.size < deliveryFilterOptions.length
                          }
                          onChange={() => {
                            setDeliveryFilters(
                              new Set(deliveryFilterOptions.map((x) => x.value))
                            );
                          }}
                        >
                          Show all
                        </ReportCheckbox>
                        <div className="tw-ml-4">
                          {deliveryFilterOptions.map((option) => (
                            <ReportCheckbox
                              key={option.value}
                              checked={deliveryFilters.has(option.value)}
                              onChange={() =>
                                setDeliveryFilters((prev) => {
                                  const newFilters = new Set(prev);
                                  if (newFilters.has(option.value)) {
                                    newFilters.delete(option.value);
                                  } else {
                                    newFilters.add(option.value);
                                  }
                                  return newFilters;
                                })
                              }
                            >
                              {option.label}
                            </ReportCheckbox>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )
              ) : (
                <>
                  {isBrandFilterEnabled && !isPredictiveEnabled && (
                    <>
                      <Separator />
                      <div className="tw-my-3 tw-px-6">
                        <div className="tw-mb-2 tw-text-sm tw-text-black/50">
                          Brand filter
                        </div>
                        <BrandingFilter />
                      </div>
                    </>
                  )}
                  {showExtendedFilters && (
                    <>
                      <Separator />
                      {growthV2License
                        ? renderGrowthFiltersTab()
                        : renderV1FiltersTab()}
                      <div className="tw-max-h-96 tw-overflow-y-auto tw-px-6 tw-pb-3 tw-transition-all">
                        {filterType === ReportsFilterType.experiences && (
                          <ExperiencesFilter />
                        )}
                        {filterType === ReportsFilterType.audiences && (
                          <AudiencesFilter />
                        )}
                        {filterType === ReportsFilterType.plays && (
                          <PlaysFilter />
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </Popover>
    </>
  );
};

const DatePickerV2ActionButton: React.FC<
  React.ComponentProps<typeof DefaultActionButton>
> = ({ onClick, disabled }) => {
  const { timeRange } = useContext(ReportsFilterContext);
  const timeValues = useSelectTimePeriods();

  const timeAgoText = timeValues.find(
    (r) => r.value === timeRange.timeAgoKey
  )?.name;

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
      <SIcon name="calendar" />
      <button
        className="tw-flex tw-w-36 tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-cbrneutral-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-cbrneutral-900 tw-transition-colors hover:tw-border-cbrneutral-300 hover:tw-bg-cbrneutral-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        type="button"
        onClick={onClick}
        disabled={disabled}
      >
        <span className="tw-truncate">
          {timeAgoText ? capitalize(timeAgoText) : 'Pick a range'}
        </span>
        <SIcon
          className="tw-ml-2 tw-h-4 tw-w-4 tw-flex-shrink-0"
          name="chevron-down"
        />
      </button>
    </div>
  );
};

export const DatePickerV2Inner: React.FC = () => {
  const { save, reset } = useContext(ReportsFilterContext);
  const isDemoData = useShowDemoData();

  const handlePopoverClose = (reason?: ModalOnCloseReason): void => {
    if (reason) setTimeout(() => reset(), 500);
  };

  const actionButton = useMemo(
    () => (localProps: React.ComponentProps<typeof DefaultActionButton>) => (
      <DatePickerV2ActionButton {...localProps} disabled={isDemoData} />
    ),
    []
  );

  return (
    <GenericPopover
      popoverProps={{
        PaperProps: {
          style: { width: '20rem', overflow: 'initial', zIndex: 1300 },
        },
        classes: { paper: 'bbk-generic-dialog-popover' },
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      actionButton={actionButton}
      onClose={handlePopoverClose}
    >
      {({ handleClose }) => {
        const handleSave = (): void => {
          save();
          handleClose();
        };

        const handleCancel = (): void => {
          reset();
          handleClose();
        };

        return (
          <div className="tw-flex tw-w-full tw-flex-col tw-py-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pb-4">
              <span className="tw-block tw-text-heading-md tw-text-cbrneutral-900">
                Date filter
              </span>
              <div>
                <SButton
                  onClick={handleCancel}
                  variant="neutral-outline"
                  size="regular"
                  disabled={isDemoData}
                >
                  Cancel
                </SButton>
                <SButton
                  onClick={handleSave}
                  variant="primary"
                  size="regular"
                  className="tw-ml-2"
                  disabled={isDemoData}
                >
                  Apply
                </SButton>
              </div>
            </div>
            <Separator />
            <span className="tw-px-6 tw-pt-4 tw-text-xs tw-font-medium">
              Date
            </span>
            <div className="tw-px-6 tw-pb-4 tw-pt-2">
              <DateFilter />
            </div>
          </div>
        );
      }}
    </GenericPopover>
  );
};

export const DatePickerV2: React.FC = () => {
  return (
    <ReportsFilterProvider>
      <div className="tw-flex tw-items-center tw-gap-2">
        <DatePickerV2Inner />
        <TimePicker hidePicker readOnly />
      </div>
    </ReportsFilterProvider>
  );
};

export const ReportsFilterPicker: React.FC<ReportsFilterProps> = (props) => (
  <ReportsFilterProvider>
    <>
      <ReportsFilter {...props} />
      <TimePicker hidePicker readOnly />
    </>
  </ReportsFilterProvider>
);

export const PredictiveBrandFilterPicker: React.FC = () => (
  <ReportsFilterProvider>
    <>
      <BrandingFilter isPredictiveEnabled />
    </>
  </ReportsFilterProvider>
);
